import React, { useCallback, useState } from 'react';
import Box from '@material-ui/core/Box';
import PlayCircleOutlineRoundedIcon from '@material-ui/icons/PlayCircleOutlineRounded';
import { StatsCard } from 'common';
import {
  CallVerbiages,
  CandidateRoleplayScenarioSelector,
  generateCandidatePresentation,
  generateJobOrderPresentation,
  getRoleplayMetrics,
  JobOrderRoleplayScenarioSelector,
  PresentationMakerTemplate,
  RoleplayFeedbackDrawer,
  RoleplayManualDrawer
} from 'features/ai-generators';
import {
  AI_ENDPOINTS,
  CALL_VERBIAGES_MAP,
  CANDIDATE_ROLEPLAY_SCENARIOS,
  JOB_ORDERS_ROLEPLAY_SCENARIOS
} from 'features/ai-generators/constants';
import { StatsItemsCard } from 'features/command-center';
import useDatatable, {
  buildRowActionsRendererDefinition,
  extractObjectFromDataTable,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import usePageTitle from 'hooks/usePageTitle';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { FPActionButton, When } from 'UI/components/atoms';
import DataTable from 'UI/components/organisms/DataTable';
import FiltersLayout from 'UI/components/templates/FiltersLayout';
import { additionalPeriodRanges } from 'UI/components/templates/SideFiltersLayout/filters';
import { backNavigateListConfig } from 'UI/constants/config';
import { PageTitles, RowActions } from 'UI/constants/defaults';
import { CandidateEntity, JobOrderEntity } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { createCustomStaticRanges } from 'UI/utils';

import { EntitySelectionModal } from './EntitySelectionModal';
import {
  COLUMNS,
  COPIES,
  ORDER_BY_OPTIONS,
  PAGE_KEY,
  ROLEPLAY_SETUP_OPTIONS
} from './Roleplay.constants';
import {
  buildPresentationMakerCandidateParam,
  buildPresentationMakerJobOrderParam,
  formatResponse,
  formatStats
} from './Roleplay.utils';

const SOURCE_PARAMS_FN_MAP = {
  [CandidateEntity.id]: buildPresentationMakerCandidateParam,
  [JobOrderEntity.id]: buildPresentationMakerJobOrderParam,
  default: () => {}
};

const SOURCE_PRESENTATION_FN_MAP = {
  [CandidateEntity.id]: generateCandidatePresentation,
  [JobOrderEntity.id]: generateJobOrderPresentation,
  default: () => {}
};

export const Roleplay = () => {
  usePageTitle({ title: PageTitles.Roleplay });
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isRoleplayCreationEnabled = checkIfFeatureFlagEnabled(
    FeatureFlags.AIRoleplayCreationFromList
  );
  const [selectedRoleplayLogId, setSelectedRoleplayLogId] = useState(null);
  const [isAIRoleplayDrawerOpen, setIsAIRoleplayDrawerOpen] = useState(false);
  const [isManualRoleplayDrawerOpen, setIsManualRoleplayDrawerOpen] = useState(false);
  const [isEntitySelectionModalOpen, setIsEntitySelectionModalOpen] = useState(false);
  const [sourceData, setSourceData] = useState(null);
  const [callType, setCallType] = useState(null);
  const initialPreferences = getColumnPreferences(PAGE_KEY, 0, ORDER_BY_OPTIONS, COLUMNS, 1);
  const buildSourceParamsFn =
    SOURCE_PARAMS_FN_MAP[sourceData?.type] || SOURCE_PARAMS_FN_MAP.default;
  const generateSourcePresentation =
    SOURCE_PRESENTATION_FN_MAP[sourceData?.type] || SOURCE_PRESENTATION_FN_MAP.default;

  const {
    columnPreferences,
    count,
    data,
    filters,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersApply,
    handleFiltersChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePeriodChange,
    handlePerPageChange,
    listState,
    selectedPeriod
  } = useDatatable({
    apiInstance: SECONDARY_API_CLIENT,
    key: PAGE_KEY,
    columnsDefinitions: COLUMNS,
    endpoint: AI_ENDPOINTS.roleplayCalls,
    entityName: 'RoleplayLogs',
    formatResponse,
    initialPreferences,
    orderByOptions: ORDER_BY_OPTIONS,
    periodDefaultValue: null,
    periodFilterColumn: 'date',
    shouldScrollOnNavigate: true
  });

  const { columns, columnOrder, orderBy, direction } = columnPreferences;
  const { isLoading, showWholeSkeleton, page, perPage, keyword } = listState;

  const handleViewRoleplayLog = async ({ rowData }) => {
    if (!rowData) return;
    const item = extractObjectFromDataTable(COLUMNS, ['id'], rowData);

    if (!item) return;
    setSelectedRoleplayLogId(item.id);
  };

  const actionButton = buildRowActionsRendererDefinition({
    actions: [{ name: RowActions.View, onClick: handleViewRoleplayLog }]
  });

  const finalColumns = getColumnsToRender([...columns, actionButton]);

  const handleOnCloseFeedbackDrawer = () => {
    setSelectedRoleplayLogId(null);
  };

  const getCachedPromises = useCallback(() => {
    return Promise.all([
      getRoleplayMetrics({
        startDate: selectedPeriod?.startDate || null,
        endDate: selectedPeriod?.endDate || null
      })
    ]).then(([responseData]) => {
      return formatStats(responseData.data);
    });
  }, [selectedPeriod]);

  const handleOpenAIRoleplayDrawer = () => setIsAIRoleplayDrawerOpen(true);
  const handleCloseAIRoleplayDrawer = () => setIsAIRoleplayDrawerOpen(false);
  const handleOpenEntitySelectionModal = () => setIsEntitySelectionModalOpen(true);
  const handleCloseEntitySelectionModal = () => setIsEntitySelectionModalOpen(false);
  const handleOpenManualRoleplayDrawer = () => setIsManualRoleplayDrawerOpen(true);
  const handleCloseManualRoleplayDrawer = () => setIsManualRoleplayDrawerOpen(false);

  const handleResetSourceData = useCallback(() => {
    setSourceData(null);
    handleOpenEntitySelectionModal();
  }, []);

  const handleSelectEntity = useCallback(
    ({ entity, selectedRoleplaySetup: newSetupOptionSelected, callType: newCallType }) => {
      const isFromInventory = newSetupOptionSelected === ROLEPLAY_SETUP_OPTIONS.fromInventory;
      setSourceData(isFromInventory ? entity : null);
      setCallType(newCallType);
      isFromInventory ? handleOpenAIRoleplayDrawer() : handleOpenManualRoleplayDrawer();
    },
    []
  );

  return (
    <FiltersLayout
      dateRangerPickerProps={{
        staticRanges: createCustomStaticRanges(additionalPeriodRanges)
      }}
      defaultRange={null}
      disableSideMenu
      disableTimeRange
      enableStore={false}
      extraSelector={
        isRoleplayCreationEnabled && (
          <Box display="flex" justifyContent="flex-end" p={1}>
            <FPActionButton
              fullWidth
              icon={PlayCircleOutlineRoundedIcon}
              onClick={handleOpenEntitySelectionModal}
              text={strings.ai.generators.roleplay.startNewSimulation}
            />
          </Box>
        )
      }
      filters={filters}
      isLoading={showWholeSkeleton}
      onFiltersChange={handleFiltersChange}
      onMenuToggle={handleFiltersToggle}
      onPeriodChange={handlePeriodChange}
      onSearch={handleFiltersApply}
      subtitle={COPIES.subtitle}
      title={COPIES.title}
      titleLabelProps={backNavigateListConfig}
    >
      <Box display="flex" flexDirection="column" gridGap={32}>
        <StatsItemsCard
          header={strings.ai.generators.roleplay.metrics.title}
          promise={getCachedPromises}
          textAlign="center"
        />

        <StatsCard header={COPIES.roleplayLogs} paddingContent={0}>
          <DataTable
            columnOrder={columnOrder?.length ? columnOrder : undefined}
            columns={finalColumns}
            count={count}
            data={data}
            draggableColumns={{
              enabled: true
            }}
            elevation={0}
            filter={false}
            loading={showWholeSkeleton}
            onColumnDisplayClick={handleColumnDisplayChange}
            onColumnOrderChange={handleColumnOrderChange}
            onColumnSortChange={handleColumnSortChange}
            onPageClick={handlePageChange}
            onPerPageClick={handlePerPageChange}
            onSearchTextChange={handleKeywordChange}
            onToggleFilters={handleFiltersToggle}
            page={page}
            refreshing={isLoading}
            rowsPerPage={perPage}
            searchText={keyword}
            selectableRows="none"
            sortOrder={{ name: orderBy, direction }}
          />
        </StatsCard>
      </Box>

      <When condition={!!selectedRoleplayLogId}>
        <RoleplayFeedbackDrawer
          callId={selectedRoleplayLogId}
          onClose={handleOnCloseFeedbackDrawer}
        />
      </When>

      <When condition={isAIRoleplayDrawerOpen && sourceData && isRoleplayCreationEnabled}>
        <PresentationMakerTemplate
          buildSourceParamsFn={buildSourceParamsFn}
          generatePresentationFn={generateSourcePresentation}
          leftContent={({ onSelectPresentationType }) =>
            sourceData?.type === CandidateEntity.id ? (
              <CandidateRoleplayScenarioSelector
                candidate={sourceData}
                onResetCandidateSelection={handleResetSourceData}
                onSelectPresentationType={onSelectPresentationType}
                showCandidateInfo
              />
            ) : (
              <JobOrderRoleplayScenarioSelector
                jobOrder={sourceData}
                onResetJobOrderSelection={handleResetSourceData}
                onSelectPresentationType={onSelectPresentationType}
                showJobOrderInfo
              />
            )
          }
          onClose={handleCloseAIRoleplayDrawer}
          rightContentFooter={({ presentationTypeId }) =>
            presentationTypeId && (
              <CallVerbiages callType={CALL_VERBIAGES_MAP[presentationTypeId]} />
            )
          }
          sourceData={sourceData}
          subtitle={strings.ai.generators.roleplay.subtitle}
          title={strings.ai.generators.roleplay.title}
        />
      </When>

      <When condition={isManualRoleplayDrawerOpen && isRoleplayCreationEnabled}>
        <RoleplayManualDrawer
          callType={callType}
          leftContent={({ presentationText }) =>
            callType === CALL_VERBIAGES_MAP.roleplayColdCallToHa ? (
              <CandidateRoleplayScenarioSelector
                defaultScenario={CANDIDATE_ROLEPLAY_SCENARIOS[0]}
                isFromManualSetup
                candidate={sourceData}
                onResetCandidateSelection={handleResetSourceData}
                presentationText={presentationText}
                showCandidateInfo
              />
            ) : (
              <JobOrderRoleplayScenarioSelector
                defaultScenario={JOB_ORDERS_ROLEPLAY_SCENARIOS[0]}
                isFromManualSetup
                jobOrder={sourceData}
                onResetJobOrderSelection={handleResetSourceData}
                presentationText={presentationText}
                showJobOrderInfo
              />
            )
          }
          onClose={handleCloseManualRoleplayDrawer}
          subtitle={strings.ai.generators.roleplay.subtitle}
          title={strings.ai.generators.roleplay.title}
        />
      </When>

      <When condition={isEntitySelectionModalOpen && isRoleplayCreationEnabled}>
        <EntitySelectionModal
          onClose={handleCloseEntitySelectionModal}
          onSelect={handleSelectEntity}
          open
        />
      </When>
    </FiltersLayout>
  );
};
